import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, Pencil, ArrowRightCircle, Plus, Globe2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { WISHLIST } from "@/constants/testIds";
import ConfirmDialog from "@/components/ConfirmDialog";
import WishlistEditor from "@/components/WishlistEditor";

const COURSE_BG =
  "/brand/proshop-shoes.jpg";

export default function WishlistPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [options, setOptions] = useState({ where_seen: [], default_collections: [], categories: [], brands: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // null | "new" | item object
  const [pendingDelete, setPendingDelete] = useState(null);
  const [collectionFilter, setCollectionFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/wishlist");
      setItems(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    api.get("/wishlist/options").then(({ data }) => setOptions(data)).catch(() => {});
    load();
  }, [load]);

  // Collections actually in use, merged with default suggestions.
  const collections = useMemo(() => {
    const inUse = Array.from(new Set(items.map((i) => i.collection).filter(Boolean)));
    const merged = Array.from(new Set([...(options.default_collections || []), ...inUse]));
    return merged;
  }, [items, options.default_collections]);

  const filtered = useMemo(() => {
    if (collectionFilter === "all") return items;
    if (collectionFilter === "_uncollected") return items.filter((i) => !i.collection);
    return items.filter((i) => (i.collection || "").toLowerCase() === collectionFilter.toLowerCase());
  }, [items, collectionFilter]);

  const convertToOrder = (item) => {
    // Stash the wishlist payload in sessionStorage so NewOrderPage can pre-fill.
    const seed = {
      item_name: item.item_name,
      category: item.category || "",
      color: item.color || "",
      size: item.size || "",
      vendor_preference: item.brand || "",
      notes: item.notes || "",
      where_seen: item.where_seen || "",
      where_seen_other: item.where_seen_other || "",
      wishlist_source_id: item.wishlist_id,
    };
    try {
      window.sessionStorage.setItem("mcc.order.seed", JSON.stringify(seed));
    } catch (err) {
      // sessionStorage can throw in private browsing / disabled storage modes —
      // the convert-to-order flow still works, we just won't pre-fill the form.
      console.warn("Could not stash wishlist seed in sessionStorage:", err?.message || err);
    }
    navigate("/orders/new");
  };

  const remove = async () => {
    if (!pendingDelete) return;
    try {
      await api.delete(`/wishlist/${pendingDelete.wishlist_id}`);
      setPendingDelete(null);
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setPendingDelete(null);
    }
  };

  return (
    <div className="bg-cream min-h-screen" data-testid={WISHLIST.page}>
      <section className="relative h-64 overflow-hidden border-b border-[#E5E3D8]">
        <img src={COURSE_BG} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#1A422D]/70" />
        <div className="absolute inset-0 flex items-end max-w-7xl mx-auto px-6 sm:px-10 py-8">
          <div className="text-[#F9F8F4] w-full">
            <div className="flex items-end justify-between gap-4 flex-wrap">
              <div>
                <div className="overline" style={{ color: "#D9C68A" }}>Members&rsquo; Lounge</div>
                <h1 className="font-serif text-4xl sm:text-5xl mt-2 leading-none">Wishlist</h1>
                <p className="mt-3 max-w-xl text-sm text-[#F9F8F4]/80">
                  Save things you&rsquo;ve seen — Pinehurst, Bandon, Scotland — and one-tap
                  convert any of them into a special order when you&rsquo;re ready.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setEditing("new")}
                data-testid={WISHLIST.addCta}
                className="inline-flex items-center gap-2 px-5 py-3 bg-[#D9C68A] text-[#1A422D] text-xs uppercase tracking-[0.22em] font-semibold hover:bg-[#E5D6A8] transition-colors"
              >
                <Plus size={14} /> Add to Wishlist
              </button>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-10 space-y-6">
        {error && (
          <div className="px-4 py-3 text-sm border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a]">{error}</div>
        )}

        {/* Collection filter */}
        {items.length > 0 && (
          <div className="flex flex-wrap gap-2">
            <FilterPill active={collectionFilter === "all"} onClick={() => setCollectionFilter("all")}>
              All ({items.length})
            </FilterPill>
            {collections.map((c) => {
              const n = items.filter((i) => (i.collection || "") === c).length;
              if (n === 0) return null;
              return (
                <FilterPill
                  key={c}
                  active={collectionFilter === c}
                  onClick={() => setCollectionFilter(c)}
                >
                  {c} ({n})
                </FilterPill>
              );
            })}
            {items.some((i) => !i.collection) && (
              <FilterPill
                active={collectionFilter === "_uncollected"}
                onClick={() => setCollectionFilter("_uncollected")}
              >
                Uncategorized
              </FilterPill>
            )}
          </div>
        )}

        {loading ? (
          <div className="overline text-[#7A7A7A]">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="surface-card p-12 text-center">
            <div className="overline">Empty Wishlist</div>
            <h2 className="font-serif text-3xl mt-2 text-[#1A1A1A]">Nothing saved yet.</h2>
            <p className="text-[#4A4A4A] mt-3 max-w-md mx-auto">
              Spotted something on your last trip? Save it here and convert to an order whenever you're ready.
            </p>
            <button type="button" onClick={() => setEditing("new")} className="btn-primary mt-6 inline-flex items-center gap-2">
              <Plus size={14} /> Add Your First Item
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((item) => (
              <Card
                key={item.wishlist_id}
                item={item}
                onEdit={() => setEditing(item)}
                onDelete={() => setPendingDelete(item)}
                onConvert={() => convertToOrder(item)}
              />
            ))}
          </div>
        )}
      </div>

      {editing && (
        <WishlistEditor
          item={editing === "new" ? null : editing}
          options={options}
          collections={collections}
          onClose={(saved) => {
            setEditing(null);
            if (saved) load();
          }}
        />
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        tone="danger"
        title={`Remove "${pendingDelete?.item_name}" from your wishlist?`}
        message="Wishlist deletes are permanent. You can re-add it later if you change your mind."
        confirmLabel="Remove"
        onConfirm={remove}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}

function FilterPill({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2 text-xs uppercase tracking-wider border transition-colors ${
        active
          ? "bg-[#1A422D] text-white border-[#1A422D]"
          : "bg-white text-[#1A422D] border-[#E5E3D8] hover:border-[#1A422D]"
      }`}
    >
      {children}
    </button>
  );
}

function Card({ item, onEdit, onDelete, onConvert }) {
  return (
    <div
      data-testid={`${WISHLIST.card}${item.wishlist_id}`}
      className="surface-card p-6 flex flex-col"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          {item.collection && <div className="overline">{item.collection}</div>}
          <h3 className="font-serif text-xl mt-1 text-[#1A1A1A]">{item.item_name}</h3>
          {item.brand && <div className="text-sm text-[#4A4A4A] mt-1">{item.brand}</div>}
        </div>
        {item.share_with_club && (
          <span title="Visible in club aggregate trends" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#1A422D] border border-[#1A422D] px-2 py-1 bg-[#E9F0EB]">
            <Globe2 size={11} /> Shared
          </span>
        )}
      </div>
      <dl className="mt-4 grid grid-cols-2 gap-x-3 gap-y-2 text-sm flex-1">
        {item.color && (<><dt className="label-elegant">Color</dt><dd className="text-[#1A1A1A]">{item.color}</dd></>)}
        {item.size && (<><dt className="label-elegant">Size</dt><dd className="text-[#1A1A1A]">{item.size}</dd></>)}
        {item.category && (<><dt className="label-elegant">Category</dt><dd className="text-[#1A1A1A]">{item.category}</dd></>)}
        {item.where_seen && (
          <>
            <dt className="label-elegant">Seen At</dt>
            <dd className="text-[#1A1A1A]">
              {item.where_seen === "Other" ? (item.where_seen_other || "Other") : item.where_seen}
            </dd>
          </>
        )}
      </dl>
      {item.notes && (
        <p className="text-sm text-[#4A4A4A] mt-3 border-t border-[#E5E3D8] pt-3 line-clamp-3">
          {item.notes}
        </p>
      )}
      <div className="mt-5 flex items-center justify-between gap-2 border-t border-[#E5E3D8] pt-4">
        <button
          type="button"
          onClick={onConvert}
          data-testid={`${WISHLIST.convert}${item.wishlist_id}`}
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-[#1A422D] hover:text-[#0F2A1C]"
        >
          <ArrowRightCircle size={14} /> Convert to Order
        </button>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onEdit}
            data-testid={`${WISHLIST.edit}${item.wishlist_id}`}
            className="p-2 text-[#7A7A7A] hover:text-[#1A422D] hover:bg-[#E9F0EB]"
            aria-label="Edit wishlist item"
            title="Edit"
          >
            <Pencil size={15} />
          </button>
          <button
            type="button"
            onClick={onDelete}
            data-testid={`${WISHLIST.delete}${item.wishlist_id}`}
            className="p-2 text-[#7A7A7A] hover:text-[#8a1a1a] hover:bg-[#fbe9e9]"
            aria-label="Remove from wishlist"
            title="Remove"
          >
            <Trash2 size={15} />
          </button>
        </div>
      </div>
    </div>
  );
}
